import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setNested = (group, k, v) => setS((p) => ({ ...p, [group]: { ...(p[group] || {}), [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Настройките са запазени"); }
    catch { toast.error("Грешка при запис"); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-charcoal/50">Зареждане...</p>;
  const inp = "w-full border border-stone px-3 py-2.5 outline-none focus:border-gold";
  const F = ({ label, children, hint }) => (
    <div><label className="block text-sm text-navy font-medium mb-1.5">{label}</label>{children}{hint && <p className="text-xs text-charcoal/45 mt-1">{hint}</p>}</div>
  );
  const Card = ({ title, children }) => (
    <div className="bg-white border border-stone p-6 space-y-4"><h2 className="font-serif text-lg text-navy">{title}</h2>{children}</div>
  );

  return (
    <form onSubmit={save} data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-navy">Настройки</h1>
        <button type="submit" disabled={saving} className="btn-gold px-6 py-2.5 rounded-sm flex items-center gap-2 text-sm" data-testid="save-settings-btn"><Save size={16} /> {saving ? "Запис..." : "Запази"}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Контакти и магазин">
          <F label="Име на сайта"><input value={s.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className={inp} /></F>
          <F label="Имейл"><input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inp} data-testid="set-email" /></F>
          <F label="Телефон"><input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inp} /></F>
          <F label="Адрес"><input value={s.address || ""} onChange={(e) => set("address", e.target.value)} className={inp} /></F>
          <F label="Бележка за доставка"><input value={s.shipping_note || ""} onChange={(e) => set("shipping_note", e.target.value)} className={inp} /></F>
        </Card>

        <Card title="Социални мрежи">
          {["facebook", "instagram", "pinterest", "youtube"].map((k) => (
            <F key={k} label={k[0].toUpperCase() + k.slice(1)}>
              <input value={s.social?.[k] || ""} onChange={(e) => setNested("social", k, e.target.value)} className={inp} placeholder="https://..." data-testid={`social-${k}`} />
            </F>
          ))}
        </Card>

        <Card title="SEO по подразбиране">
          <F label="Заглавие (title)"><input value={s.seo?.title || ""} onChange={(e) => setNested("seo", "title", e.target.value)} className={inp} data-testid="seo-title" /></F>
          <F label="Описание (meta description)"><textarea value={s.seo?.description || ""} onChange={(e) => setNested("seo", "description", e.target.value)} rows={3} className={inp} /></F>
          <F label="Ключови думи"><input value={s.seo?.keywords || ""} onChange={(e) => setNested("seo", "keywords", e.target.value)} className={inp} /></F>
          <F label="OG изображение (URL)"><input value={s.seo?.og_image || ""} onChange={(e) => setNested("seo", "og_image", e.target.value)} className={inp} /></F>
        </Card>

        <Card title="Анализи и пиксели">
          <F label="Google Analytics ID" hint="напр. G-XXXXXXX"><input value={s.analytics?.google_analytics_id || ""} onChange={(e) => setNested("analytics", "google_analytics_id", e.target.value)} className={inp} data-testid="ga-id" /></F>
          <F label="Meta Pixel ID" hint="Facebook пиксел"><input value={s.analytics?.meta_pixel_id || ""} onChange={(e) => setNested("analytics", "meta_pixel_id", e.target.value)} className={inp} data-testid="pixel-id" /></F>
          <F label="Google Tag Manager ID" hint="напр. GTM-XXXX"><input value={s.analytics?.google_tag_manager_id || ""} onChange={(e) => setNested("analytics", "google_tag_manager_id", e.target.value)} className={inp} /></F>
        </Card>

        <Card title="Промо попъп (събиране на имейли)">
          <label className="flex items-center gap-3 text-sm text-navy"><input type="checkbox" checked={!!s.popup?.enabled} onChange={(e) => setNested("popup", "enabled", e.target.checked)} className="accent-[#C6A45C] w-4 h-4" data-testid="popup-enabled" /> Активен</label>
          <F label="Заглавие"><input value={s.popup?.title || ""} onChange={(e) => setNested("popup", "title", e.target.value)} className={inp} /></F>
          <F label="Подзаглавие"><input value={s.popup?.subtitle || ""} onChange={(e) => setNested("popup", "subtitle", e.target.value)} className={inp} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Забавяне (сек)"><input type="number" value={s.popup?.delay || 12} onChange={(e) => setNested("popup", "delay", parseInt(e.target.value) || 0)} className={inp} /></F>
            <F label="Промо код"><input value={s.popup?.code || ""} onChange={(e) => setNested("popup", "code", e.target.value.toUpperCase())} className={inp} data-testid="popup-code-setting" /></F>
          </div>
          <p className="text-xs text-charcoal/45">Уверете се, че въведеният код съществува като активен купон в меню „Купони".</p>
        </Card>

        <Card title="Плащане">
          <label className="flex items-center gap-3 text-sm text-navy"><input type="checkbox" checked={s.cod_enabled !== false} onChange={(e) => set("cod_enabled", e.target.checked)} className="accent-[#C6A45C] w-4 h-4" /> Наложен платеж (активен)</label>
          <label className="flex items-center gap-3 text-sm text-navy"><input type="checkbox" checked={!!s.stripe_enabled} onChange={(e) => set("stripe_enabled", e.target.checked)} className="accent-[#C6A45C] w-4 h-4" data-testid="stripe-toggle" /> Stripe плащане с карта (подготвено)</label>
          <p className="text-xs text-charcoal/45">Stripe интеграцията е подготвена за активиране при добавяне на API ключове.</p>
        </Card>
      </div>
    </form>
  );
}
